import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { AuthInput, AuthButton } from "./AuthLayout";
import { forgotPassword } from "../api/auth";

export function ForgotPasswordForm({ onSuccess }: { onSuccess: (email: string) => void }) {
    const { t } = useTranslation();

    const schema = z.object({
        email: z.string().email(t('auth.forgotPassword.errors.invalidEmail')),
    });

    type FormData = z.infer<typeof schema>;

    const { register, handleSubmit, formState: { errors }, setError } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const [loading, setLoading] = useState(false);

    const onSubmit = async (data: FormData) => {
        setLoading(true);
        try {
            await forgotPassword(data.email);
            onSuccess(data.email);
        } catch (e: any) {
            console.error(e);
            setError("root", { message: e.response?.data?.message || t('auth.forgotPassword.errors.sendFailed') });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center">
            <h1 className="font-bold text-3xl text-[#338585] mb-4">{t('auth.forgotPassword.title')}</h1>
            <p className="text-gray-500 mb-8 text-center text-sm">
                {t('auth.forgotPassword.subtitle')}
            </p>

            <AuthInput
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                {...register("email")}
                error={errors.email?.message}
            />

            {errors.root && <div className="text-red-500 mb-4 text-sm">{errors.root.message}</div>}

            <AuthButton disabled={loading}>
                {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.resetButton')}
            </AuthButton>
        </form>
    );
}
